import { useMemo, useRef, useState } from 'react'

import { t } from 'i18next'
import { DataItem } from 'scrivito'

import {
  generateDescriptionText,
  hasFilterData,
  reviewDescriptionText,
} from './descriptionAi'
import { AiAttributionOrigin } from '@/Atomic/Molecules/AiAttributionBadge'
import { errorToast, showToast } from '@/Components/Toast'
import { ToastMessageType } from '@/Components/Toast/const'
import { useEntitySchema } from '@/hooks/useEntitySchema'
import { FieldMap } from '@/utils/CRUD/read/dataItem/types'

type SparkleAction = 'generate' | 'review' | 'menu' | 'disabled'

interface LastAiText {
  text: string
  origin: AiAttributionOrigin
}

/**
 * Drives the description field's Generate/Review/edit state machine
 * (see CRM-1327 implementation notes, section 2, for the exact rules this
 * follows). `lastAiText` is session-local by design — the backend has no
 * field to persist provenance, so it resets on page reload.
 */
export const useAiDescriptionField = (
  dataItem: DataItem,
  fieldMap: FieldMap,
) => {
  const entityName =
    (dataItem.get('entityType') as string | undefined)?.toLowerCase() ??
    'person'
  const { attributes: baseSchemaAttributes, relations: schemaRelations } =
    useEntitySchema(entityName)

  const storedDescription = (dataItem.get('description') as string) || ''
  const isReadonly = fieldMap.fieldSchema?.readonly ?? false
  const characterLimit = fieldMap.fieldSchema?.maxLength

  const [isEditing, setIsEditing] = useState(false)
  const [draftText, setDraftText] = useState('')
  // Persists across confirms — needed by the click-classification below
  // ("still matches the last Generate output"). Never reset just by
  // (re)opening the editor.
  const [lastAiText, setLastAiText] = useState<LastAiText | null>(null)
  // Scoped to the current, unconfirmed editing session only — drives the
  // attribution badge. Unlike `lastAiText`, this resets whenever a fresh
  // editing session starts, so reopening an already-validated description
  // never resurrects a badge for it (CRM-1327 section 4: "Any validated
  // description -> No badge").
  const [activeProposal, setActiveProposal] = useState<LastAiText | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const sessionSnapshotRef = useRef<LastAiText | null>(null)

  const hasFilters = hasFilterData(dataItem)
  const currentText = isEditing ? draftText : storedDescription
  const canGenerate = hasFilters
  const canReview = currentText !== ''

  const action: SparkleAction = useMemo(() => {
    if (!canGenerate && !canReview) return 'disabled'
    if (currentText === '') return canGenerate ? 'generate' : 'disabled'
    if (canGenerate && lastAiText?.origin === 'generate') {
      if (isEditing) return 'generate'
      if (currentText === lastAiText.text) return 'generate'
    }
    if (canGenerate && canReview) return 'menu'
    return canGenerate ? 'generate' : 'review'
  }, [canGenerate, canReview, currentText, isEditing, lastAiText])

  const startEditing = () => {
    if (isEditing) return
    sessionSnapshotRef.current = lastAiText
    setDraftText(storedDescription)
    setActiveProposal(null)
    setIsEditing(true)
  }

  const handleGenerate = async () => {
    if (!hasFilters) return
    startEditing()
    setIsGenerating(true)
    try {
      const result = await generateDescriptionText(
        dataItem,
        entityName,
        baseSchemaAttributes,
        schemaRelations,
      )
      if (!result) {
        showToast(ToastMessageType.ERROR, t('list.aiEmptyResponse'))
        return
      }
      setDraftText(result)
      const proposal: LastAiText = { text: result, origin: 'generate' }
      setLastAiText(proposal)
      setActiveProposal(proposal)
    } catch (error) {
      errorToast(error as Error)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleReview = async () => {
    const textToReview = isEditing ? draftText : storedDescription
    if (!textToReview) return
    startEditing()
    setIsGenerating(true)
    try {
      const result = await reviewDescriptionText(textToReview)
      if (!result) {
        showToast(ToastMessageType.ERROR, t('list.aiEmptyResponse'))
        return
      }
      setDraftText(result)
      const proposal: LastAiText = { text: result, origin: 'review' }
      setLastAiText(proposal)
      setActiveProposal(proposal)
    } catch (error) {
      errorToast(error as Error)
    } finally {
      setIsGenerating(false)
    }
  }

  /** Passed as `onBeforeToggle` to the trigger's DropdownMenu — returning
   * `false` performs the action directly instead of opening the menu. */
  const handleSparkleClick = (): boolean => {
    if (action === 'menu') return true
    if (action === 'generate') {
      handleGenerate()
      return false
    }
    if (action === 'review') {
      handleReview()
      return false
    }
    return false
  }

  const handleConfirm = async () => {
    const trimmed = draftText.trim()
    try {
      await dataItem.update({ description: trimmed })
    } catch (error) {
      errorToast(error as Error)
      return
    }
    if (trimmed === '') {
      setLastAiText(null)
    }
    setActiveProposal(null)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setLastAiText(sessionSnapshotRef.current)
    setActiveProposal(null)
    setIsEditing(false)
  }

  return {
    storedDescription,
    isReadonly,
    characterLimit,
    isEditing,
    draftText,
    setDraftText,
    activeProposal,
    isGenerating,
    action,
    startEditing,
    handleSparkleClick,
    handleGenerate,
    handleReview,
    handleConfirm,
    handleCancel,
  }
}
